<template>
    <div class="flex flex-wrap mb-6">
        <loading :show="show" :label="label"> </loading>

        <!-- Pending Transactions Alert -->
        <div v-if="pendingTransactions.length > 0" class="w-full px-3 mb-6">
            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                <div class="flex items-center justify-between mb-3">
                    <h3 class="font-semibold text-yellow-800">
                        <i class="bx bx-time-five mr-1"></i>
                        Pending Transactions ({{ pendingTransactions.length }})
                    </h3>
                    <button
                        @click="loadPendingTransactions"
                        class="text-yellow-600 hover:text-yellow-800 text-sm"
                    >
                        <i class="bx bx-refresh"></i> Refresh
                    </button>
                </div>
                <div class="space-y-2 max-h-48 overflow-y-auto">
                    <div
                        v-for="tx in pendingTransactions"
                        :key="tx.id"
                        class="bg-white p-3 rounded border border-yellow-200 flex justify-between items-center"
                    >
                        <div>
                            <p class="font-medium text-gray-800">{{ tx.meter_serial }} - {{ tx.customer_name }}</p>
                            <p class="text-sm text-gray-500">
                                GMD {{ tx.total_payment }} | Retry {{ tx.retry_count }}/{{ tx.max_retries || 5 }}
                            </p>
                            <p v-if="tx.last_error" class="text-xs text-red-500">{{ tx.last_error }}</p>
                        </div>
                        <button
                            v-if="tx.can_retry"
                            @click="retryTransaction(tx)"
                            :disabled="retryingId === tx.id"
                            class="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
                        >
                            <i v-if="retryingId === tx.id" class="bx bx-loader-alt animate-spin"></i>
                            <span v-else>Retry</span>
                        </button>
                        <span v-else class="text-red-500 text-sm">Max retries</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Form -->
        <div class="w-full md:w-2/3 px-3 mb-6 md:mb-0">
            <div class="bg-white rounded-lg shadow p-6">
                <!-- Meter Number Input -->
                <div class="border-b pb-6 mb-6">
                    <label class="block text-gray-700 text-sm font-bold mb-3">
                        Enter Meter Number
                    </label>
                    <PincodeInput
                        v-model="form.meterNumber"
                        :length="11"
                        placeholder="0"
                    />
                </div>

                <!-- Customer Info -->
                <div class="flex flex-wrap -mx-3 mb-6">
                    <div class="w-full md:w-1/2 px-3 mb-4 md:mb-0">
                        <label class="block text-gray-700 text-xs font-bold uppercase mb-2">
                            Meter Number
                        </label>
                        <input
                            tabindex="-1"
                            v-model="form.meterNumber"
                            class="appearance-none block w-full bg-gray-100 text-gray-700 border rounded py-3 px-4 leading-tight"
                            type="number"
                            readonly
                        />
                    </div>
                    <div class="w-full md:w-1/2 px-3">
                        <label class="block text-gray-700 text-xs font-bold uppercase mb-2">
                            Customer's Name
                        </label>
                        <input
                            tabindex="-1"
                            v-model="form.meterName"
                            class="appearance-none block w-full bg-gray-100 text-gray-700 border rounded py-3 px-4 leading-tight"
                            type="text"
                            readonly
                        />
                        <p class="text-sm text-gray-500 mt-2">
                            Confirm with the buyer if this name matches the meter.
                        </p>
                    </div>
                </div>

                <!-- Amount Section -->
                <div class="flex flex-col items-end border-t pt-6">
                    <div class="w-72 mb-4">
                        <label class="block text-gray-700 text-xs font-bold uppercase mb-2">Amount</label>
                        <input
                            class="appearance-none block w-full bg-gray-50 font-bold text-2xl text-right text-gray-700 border rounded py-3 px-4"
                            v-model="form.amount"
                            type="number"
                            placeholder="0.00"
                        />
                    </div>
                    <div class="w-72 mb-4">
                        <label class="block text-gray-700 text-xs font-bold uppercase mb-2">Cashpower Amount</label>
                        <input
                            tabindex="-1"
                            class="appearance-none block w-full bg-gray-50 font-bold text-xl text-right text-gray-700 border rounded py-3 px-4"
                            :value="`GMD ${form.cp_amount}`"
                            type="text"
                            readonly
                        />
                    </div>
                    <div class="w-72 mb-4">
                        <label class="block text-gray-700 text-xs font-bold uppercase mb-2">Transaction Fee</label>
                        <input
                            tabindex="-1"
                            class="appearance-none block w-full bg-gray-50 font-bold text-xl text-right text-gray-700 border rounded py-3 px-4"
                            :value="`GMD ${form.fee}`"
                            type="text"
                            readonly
                        />
                    </div>
                    <div class="w-72 mb-4">
                        <label class="block text-gray-700 text-xs font-bold uppercase mb-2">Total</label>
                        <p class="text-3xl font-bold text-green-600 text-right">GMD {{ form.amount || '0.00' }}</p>
                    </div>
                    <div class="w-72">
                        <button
                            :disabled="form.meterNumber.length < 7"
                            @click="consumerCheck()"
                            class="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Receipt Panel -->
        <div class="w-full md:w-1/3 px-3">
            <div class="bg-white rounded-lg shadow">
                <div class="border-b py-4 px-4 flex justify-center">
                    <button
                        @click="print()"
                        class="text-gray-700 font-bold py-2 px-4 border border-gray-400 rounded hover:bg-gray-100"
                    >
                        <i class="bx bx-printer mr-1"></i> Print
                    </button>
                </div>
                <div class="p-4">
                    <div v-if="!receipt" class="text-center py-8 text-gray-400">
                        <i class="bx bx-receipt text-4xl mb-2"></i>
                        <p>No receipt yet</p>
                    </div>
                    <div
                        v-if="receipt"
                        id="section-to-print"
                        class="flex justify-center"
                    >
                        <recipet
                            :isLoading="showReceipt"
                            :data="receipt"
                            :user="user"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from 'vform';
import moment from 'moment';
import Swal from 'sweetalert2';
import axios from 'axios';
import PincodeInput from 'vue-pincode-input';
import Recipet from '../../../components/Recipet.vue';
// import TransactionNotice from '../../../components/TransactionNotice.vue';
import JSPM from '../../../../public/JSPrintManager';
import loading from 'vue-full-loading';

export default {
    props: ['user', 'serviceStatus'],

    components: {
        Recipet,
        loading,
        PincodeInput,
    },

    data: () => ({
        applyPaymentRes: null,
        pendingTransactions: [],
        retryingId: null,

        form: new Form({
            amount: null,
            meterNumber: '',
            meterName: '',
            fee: '',
            cp_amount: '',
            phone: null,
            consumerResponse: null,
        }),
        clear: false,
        isVending: false,
        showReceipt: false,
        showTransactionNotice: false,
        isComplate: '',
        receipt: null,
        confirmAmount: '',
        checkConsumer: false,
        isLoading: false,
        show: false,
        print2default: true,
        selectedPrint: null,
        label: 'Loading...',
    }),

    methods: {
        closeNotice() {
            this.showTransactionNotice = false;
        },

        // async checkServiceStatus() {
        //     this.isLoading = true;

        //     try {
        //         const { data } = await axios.get('/service-status/NAWEC');
        //         this.serviceStatus = data.is_live;

        //         if (!this.serviceStatus) {
        //             Swal.fire({
        //                 icon: 'error',
        //                 title: 'Service Unavailable!',
        //                 text: data.message,
        //             });
        //             this.isLoading = false;
        //             this.show = false;
        //             return false;
        //         }
        //         return true;
        //     } catch (error) {
        //         console.error('Service status check failed', error?.response);
        //         // if (error?.response.status === 401) {
        //         //     $router.push({ name: 'login' });
        //         // }
        //         Swal.fire({
        //             icon: 'error',
        //             title: 'Error!',
        //             text: 'Could not verify service status. Please try again later.....',
        //         });
        //         this.isLoading = false;
        //         this.show = false;
        //         return false;
        //     }
        // },
        async vend() {
            // navigator.geolocation.getCurrentPosition(setPosition);

            Swal.fire({
                title: '<span style="color: #1f2937; font-size: 1.5rem; font-weight: 700;">Confirm Transaction</span>',
                html: `
                    <div style="text-align: left; padding: 0.5rem 0;">
                        <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 1rem; margin-bottom: 1rem;">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
                                <span style="color: #6b7280;">Meter Number</span>
                                <span style="font-weight: 700; color: #1f2937;">${this.form.meterNumber}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
                                <span style="color: #6b7280;">Customer</span>
                                <span style="font-weight: 700; color: #1f2937;">${this.form.meterName}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between;">
                                <span style="color: #6b7280;">Location</span>
                                <span style="font-weight: 600; color: #6b7280;">${this.form.consumerResponse.serviceAddress}</span>
                            </div>
                        </div>
                        
                        <div style="background: #fafafa; border-radius: 8px; padding: 1rem; margin-bottom: 1rem;">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
                                <span style="color: #6b7280;">Amount</span>
                                <span style="font-weight: 700; color: #1f2937;">GMD ${this.form.amount}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
                                <span style="color: #6b7280;">Fee</span>
                                <span style="font-weight: 600; color: #6b7280;">GMD ${this.form.consumerResponse.fee}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 0.5rem;">
                                <span style="color: #6b7280;">Cashpower Amount</span>
                                <span style="font-weight: 700; color: #16a34a;">GMD ${this.form.amount - this.form.consumerResponse.fee}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; padding-top: 0.5rem; border-top: 1px dashed #d1d5db;">
                                <span style="color: #6b7280;">Units</span>
                                <span style="font-weight: 700; color: #16a34a; font-size: 1.1rem;">${this.form.consumerResponse.units} kWh</span>
                            </div>
                        </div>
                        
                        ${this.form?.phone ? `<div style="background: #f3f4f6; padding: 0.75rem; border-radius: 6px; margin-bottom: 1rem;"><span style="color: #6b7280;">Phone:</span> <strong>${this.form.phone}</strong></div>` : `
                        <div style="margin-bottom: 1rem;">
                            <label style="display: block; color: #374151; font-size: 0.875rem; font-weight: 600; margin-bottom: 0.5rem;">Customer Phone Number</label>
                            <input id="phone" type="tel" placeholder="Enter phone number" style="width: 100%; padding: 0.75rem 1rem; border: 1px solid #d1d5db; border-radius: 8px; font-size: 1rem; outline: none; transition: border-color 0.2s;" onfocus="this.style.borderColor='#22c55e'" onblur="this.style.borderColor='#d1d5db'" />
                        </div>
                        `}
                        
                        <div style="background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px; padding: 1rem;">
                            <label style="display: block; color: #92400e; font-size: 0.875rem; font-weight: 600; margin-bottom: 0.5rem;">⚠️ Confirm Amount</label>
                            <input id="confirm-amount" type="number" placeholder="Re-enter amount to confirm" style="width: 100%; padding: 0.75rem 1rem; border: 2px solid #fcd34d; border-radius: 8px; font-size: 1.1rem; font-weight: 700; text-align: center; outline: none; transition: border-color 0.2s;" onfocus="this.style.borderColor='#f59e0b'" onblur="this.style.borderColor='#fcd34d'" />
                        </div>
                    </div>
                `,
                preConfirm: () => {
                    if (document.getElementById('phone')) {
                        return [
                            document.getElementById('phone').value,
                            document.getElementById('confirm-amount').value,
                        ];
                    } else {
                        return [
                            document.getElementById('confirm-amount').value,
                        ];
                    }
                },
                customClass: {
                    popup: 'swal-wide',
                    confirmButton: 'swal-confirm-btn',
                    cancelButton: 'swal-cancel-btn'
                },
                width: '450px',
                showCancelButton: true,
                confirmButtonColor: '#22c55e',
                cancelButtonColor: '#ef4444',
                confirmButtonText: '✓ Yes, Confirm',
                cancelButtonText: 'Cancel',
            }).then(result => {
                console.log('---REsult', result);
                console.log('resul lenght', result.length);
                let amount;
                if (result.value.length > 1) {
                    this.form.phone = result.value[0];
                    amount = result.value[1];
                } else {
                    amount = result.value[0];
                }

                console.log('this is the phone number', this.form.phone);
                // console.log("Amount", this.form.amount, "Confirm",this.confirmAmount)
                if (this.form.amount !== amount) {
                    // Swal.fire({
                    //   icon: "error",
                    //   title: "Something went wrong!",
                    //   text: "Please enter correct amount",
                    // });
                    this.show = false;
                    this.isLoading = false;
                    this.clearForm();
                    return false;
                }
                if (result) {
                    console.log('inside vend');
                    console.log('inside vend', result.value);
                    console.log('inside vend resulrt', result);
                    this.isLoading = true;
                    this.show = true;

                    axios.post('/vendcashpower', {
                        consumerResponse: this.form.consumerResponse,
                        meterNumber: this.form.meterNumber,
                        cp_amount: this.form.cp_amount,
                        fee: this.form.fee,
                        total_amount: this.form.amount,
                        customerName: this.form.meterName,
                        phone: this.form.phone,
                        // simulate_timeout: true
                    })
                        .then(response => {
                            console.log('Vend response:', response);
                            const statusCode = response.status;
                            const data = response.data;

                            // Handle 200 OK - Success
                            // Transaction data is directly in response.data (not data.data)
                            if (statusCode === 200 && data.responseCode !== 202 && data.transaction_status === 'PROCESSED') {
                                this.applyPaymentRes = data;
                                this.receipt = data;
                                this.showReceipt = true;
                                this.print(this.receipt);
                                this.show = false;
                                this.isLoading = false;
                                this.clearForm();
                                // Refresh pending list in case it was a retry
                                this.loadPendingTransactions();
                                return;
                            }

                            // Handle 202 Accepted - Pending (can come as 200 with responseCode 202)
                            if (statusCode === 202 || data.responseCode === 202 || data.transaction_status === 'PENDING') {
                                this.show = false;
                                this.isLoading = false;
                                this.clearForm();
                                
                                Swal.fire({
                                    icon: 'warning',
                                    title: 'Transaction Pending',
                                    html: `
                                        <p>${data.message || 'NAWEC is currently unavailable. Your transaction has been saved.'}</p>
                                        <p class="mt-2 text-sm text-gray-600">
                                            Retry attempt: ${data.retry_count || 0}/5
                                        </p>
                                        ${data.last_error ? `<p class="mt-2 text-sm text-red-500">${data.last_error}</p>` : ''}
                                    `,
                                    confirmButtonText: 'View Pending Transactions',
                                    showCancelButton: true,
                                    cancelButtonText: 'Close',
                                }).then(result => {
                                    if (result.isConfirmed) {
                                        this.loadPendingTransactions();
                                    }
                                });
                                
                                // Refresh pending transactions list
                                this.loadPendingTransactions();
                                return;
                            }

                            // Handle success without explicit transaction_status (fallback)
                            if (statusCode === 200 && data.id && data.list_tokens) {
                                this.applyPaymentRes = data;
                                this.receipt = data;
                                this.showReceipt = true;
                                this.print(this.receipt);
                                this.show = false;
                                this.isLoading = false;
                                this.clearForm();
                                this.loadPendingTransactions();
                                return;
                            }
                        })
                        .catch(error => {
                            console.log('Vend error:', error);
                            const response = error.response;
                            
                            if (!response) {
                                this.show = false;
                                this.isLoading = false;
                                Swal.fire('Error!', 'Network error. Please try again.', 'error');
                                return;
                            }

                            const statusCode = response.status;
                            const data = response.data;

                            // Handle 504 Gateway Timeout - Failed after max retries
                            if (statusCode === 504 || data.responseCode === 504) {
                                this.show = false;
                                this.isLoading = false;
                                this.clearForm();
                                
                                Swal.fire({
                                    icon: 'error',
                                    title: 'Transaction Failed',
                                    html: `
                                        <p>${data.error || 'Maximum retry attempts exceeded.'}</p>
                                        <p class="mt-2 text-sm text-gray-600">
                                            Transaction has been marked as failed. No charge was applied.
                                        </p>
                                    `,
                                });
                                
                                // Refresh pending list to remove the failed transaction
                                this.loadPendingTransactions();
                                return;
                            }

                            // Handle other errors
                            this.show = false;
                            this.isLoading = false;
                            this.clearForm();
                            Swal.fire('Error!', data.error || data.message || 'An error occurred', 'error');
                        });
                }
            });
        },

        async retryTransaction(transaction) {
            this.retryingId = transaction.id;
            
            try {
                const response = await axios.post(`/cashpower/retry/${transaction.id}`);
                const statusCode = response.status;
                const data = response.data;

                // Handle 200 OK - Success (check responseCode isn't 202)
                if ((statusCode === 200 && data.responseCode !== 202) || data.responseCode === 200) {
                    this.receipt = data.data;
                    this.showReceipt = true;
                    this.print(this.receipt);
                    
                    Swal.fire({
                        icon: 'success',
                        title: 'Transaction Successful!',
                        text: 'Token has been generated successfully.',
                    });
                    
                    // Remove from pending list
                    this.pendingTransactions = this.pendingTransactions.filter(
                        tx => tx.id !== transaction.id
                    );
                }
                // Handle 202 Accepted - Still Pending (can come as 200 with responseCode 202)
                else if (statusCode === 202 || data.responseCode === 202) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Still Pending',
                        html: `
                            <p>${data.message || 'NAWEC is still unavailable. Please try again later.'}</p>
                            <p class="mt-2 text-sm text-gray-600">
                                Retry attempt: ${data.data?.retry_count || 0}/${data.data?.max_retries || 5}
                            </p>
                            ${data.data?.error ? `<p class="mt-2 text-sm text-red-500">${data.data.error}</p>` : ''}
                        `,
                    });
                    
                    // Update the transaction in the list
                    const index = this.pendingTransactions.findIndex(tx => tx.id === transaction.id);
                    if (index !== -1) {
                        this.pendingTransactions[index] = {
                            ...this.pendingTransactions[index],
                            retry_count: data.data?.retry_count || this.pendingTransactions[index].retry_count,
                            can_retry: data.data?.can_retry ?? true,
                            last_error: data.data?.error || null,
                        };
                    }
                }
            } catch (error) {
                const response = error.response;
                
                if (!response) {
                    Swal.fire('Error!', 'Network error. Please try again.', 'error');
                    this.retryingId = null;
                    return;
                }

                const statusCode = response.status;
                const data = response.data;

                // Handle 504 Gateway Timeout - Failed
                if (statusCode === 504 || data.responseCode === 504) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Transaction Failed',
                        html: `
                            <p>${data.error || 'Maximum retry attempts exceeded.'}</p>
                            <p class="mt-2 text-sm text-gray-600">
                                No charge was applied to the account.
                            </p>
                        `,
                    });
                    
                    // Remove from pending list
                    this.pendingTransactions = this.pendingTransactions.filter(
                        tx => tx.id !== transaction.id
                    );
                }
                // Handle 400, 403, 404 errors
                else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: data.error || data.message || 'An error occurred while retrying the transaction.',
                    });
                }
            }
            
            this.retryingId = null;
        },

        async loadPendingTransactions() {
            try {
                const response = await axios.get('/cashpower/pending');
                this.pendingTransactions = response.data.data || [];
            } catch (error) {
                console.error('Error loading pending transactions:', error);
            }
        },

        formatDate(dateString) {
            return moment(dateString).format('Do MMM, YYYY, h:mm a');
        },

        async reqStsToken(payload) {
            // console.log("payload", payload);
            // console.log("Apply Payment", this.applyPaymentRes);

            try {
                const response = await axios.get(
                    `/tx/get_sts_token/${payload.data.transaction_id}`
                );

                // Check if the request has token
                if (response.data.data.status == true) {
                    this.showReceipt = true;
                    this.receipt = response.data.data;

                    this.show = false;
                    this.isLoading = false;
                    this.print(this.receipt);
                } else {
                    // User retry
                    this.isLoading = false;
                    this.show = false;

                    Swal.fire({
                        title: 'Processing Transaction?',
                        text: 'We are processing your transaction in the background due to a slight delay at NAWEC. You can use the Retry button or check from your transactions list. Please avoid creating new transactions with the same meter number until this one completes.',
                        showDenyButton: true,
                        showCancelButton: true,
                        confirmButtonText: `Retry`,
                        denyButtonText: `Continue`,
                    }).then(result => {
                        /* Read more about isConfirmed, isDenied below */
                        if (result.isConfirmed) {
                            this.isLoading = true;
                            this.show = true;

                            this.reqStsToken(this.applyPaymentRes);
                        } else if (result.isDenied) {
                            // Swal.fire("Changes are not saved", "", "info");
                        }
                    });
                }

                return true;
            } catch (e) {
                console.log('Error');
                this.isLoading = false;
                this.show = false;

                return false;
            }
        },

        clearForm() {
            this.form.amount = null;
            this.form.meterNumber = '';
            this.form.meterName = '';
            this.form.fee = '';
            this.form.cp_amount = '';
            this.form.conclearsumerResponse = null;
            this.form.phone = null;
            // this.showReceipt = false;
            this.confirmAmount = null;
            // this.receipt = n;
            this.clear = false;
        },

        async consumerCheck() {

            this.receipt = null;
            this.showReceipt = false;

            this.isLoading = true;
            this.show = true;
            try {
                const response = await this.form.post('/calculate_payment');

                // console.log("RESPONSE", response.data);

                this.form.consumerResponse = response.data.data;
                this.form.meterName = this.form.consumerResponse.CustomerName;
                this.form.fee = this.form.consumerResponse.fee;
                this.form.cp_amount =
                    this.form.amount - this.form.consumerResponse.fee;
                this.form.phone = response.data.data.phone;
                this.checkConsumer = true;

                this.isLoading = false;
                this.show = false;
                // this.$router.push({ path: "/agents" });

                this.vend();
                return true;
            } catch (e) {
                this.isLoading = false;
                this.show = false;

                console.log('Error', e?.response);

                return false;
            }
        },

        print: function () {
            try {
                if (this.selected_printer === '' && !this.print2default) {
                    alert('You must select a printer');
                    return;
                }

                console.log('checking....');
                var cpj = new JSPM.ClientPrintJob();
                console.log('cps test', cpj);
                if (this.receipt) {
                    if (this.print2default) {
                        console.log('opppps okay');
                        cpj.clientPrinter = new JSPM.DefaultPrinter();
                    } else {
                        console.log('cheking else');
                        cpj.clientPrinter = new JSPM.InstalledPrinter(
                            this.selected_printer
                        );
                    }

                    var day = new Date(this.receipt.created_at);
                    console.log('its has reach hereeeee', day);

                    var dateCreated = moment()
                        .utc(day)
                        .format('Do MMM, YYYY, h:mm:ss a');

                    var esc = '\x1B';
                    var newLine = '\x0A';
                    var fullCut = esc + 'm';

                    var cmds = esc + '@';
                    cmds += esc + '!' + '\x38';
                    cmds += 'CHAPMAN XPRESS';
                    cmds += newLine;
                    cmds += esc + '!' + '\x00';

                    cmds += newLine + newLine;

                    cmds += this.user.data.staff.branch;
                    cmds += newLine;
                    cmds += `Receipt No .: ${this.receipt.receipt}`;
                    cmds += newLine + newLine;
                    cmds += `Meter No .: ${this.receipt.meter_serial}`;
                    cmds += newLine;

                    cmds += esc + '!' + '\x22';
                    cmds += `${this.receipt.customer_name}`;
                    cmds += esc + '!' + '\x00';

                    cmds += newLine;
                    cmds += `${this.receipt.meter_serial}`;
                    cmds += newLine + newLine;
                    cmds += '------------------ Token Number ---------------';
                    cmds += newLine;
                    cmds += esc + '1' + '\x01  ';

                    cmds += newLine;
                    cmds += '********************************************';
                    cmds += newLine + newLine;
                    cmds += esc + '!' + '\x30';

                    // if (
                    //     this.receipt.list_tokens &&
                    //     this.receipt.list_tokens.length > 0
                    // ) {
                    //     const formattedTokens = this.receipt.list_tokens.map(
                    //         token => this.formatToken(token)
                    //     );
                    //     cmds += formattedTokens.join(', ');
                    // }

                    if (this.receipt.list_tokens) {
                        // Parse list_tokens if it's a JSON string
                        let tokens = this.receipt.list_tokens;
                        if (typeof tokens === 'string') {
                            try {
                                tokens = JSON.parse(tokens);
                            } catch (e) {
                                tokens = [tokens];
                            }
                        }
                        if (tokens && tokens.length > 0) {
                            const formattedTokens = tokens.map(
                                token => this.formatToken(token)
                            );
                            cmds += '\n';
                            cmds += formattedTokens.join('\n');
                        }
                    }

                    cmds += newLine + newLine;
                    cmds += esc + '!' + '\x00';

                    cmds += '********************************************';
                    cmds += newLine + newLine;

                    cmds += `Amount Paid .:       GMD ${this.receipt.total_payment}`;
                    cmds += newLine;
                    cmds += `Fee .:               GMD ${this.receipt.fee}`;
                    cmds += newLine;
                    cmds += `Cashpower Amount .:  GMD ${this.receipt.amount}`;
                    cmds += newLine;
                    cmds += `Total Units .:       ${this.receipt.units}kWh`;
                    cmds += newLine;

                    cmds += `Payment Date .:      ${dateCreated}`;

                    cmds += newLine + newLine;
                    cmds += esc + '!' + '\x18';
                    cmds += `Operator: ${this.user.data.name}`;
                    cmds += newLine;
                    cmds += esc + '!' + '\x00';

                    cmds += 'Use Energy Wisely!';
                    cmds += newLine;

                    cmds += newLine;
                    cmds += `Phone: +220 438 3223 - 307 4455 - ${this.user.data.staff.branch_contact_phone}`;
                    cmds += newLine + newLine;
                    cmds += newLine + newLine;

                    cmds += fullCut;

                    cpj.printerCommands = cmds;
                    cpj.sendToClient();
                    console.log('its has reach hereeeee');
                }
            } catch (error) {
                console.log('error in print', error);
            }
        },

        formatToken(token) {
            return token.match(/\d{4}(?=\d{2,3})|\d+/g).join('-');
        },

        onInit: function () {
            var _this = this;
            JSPM.JSPrintManager.auto_reconnect = true;
            JSPM.JSPrintManager.start();
            JSPM.JSPrintManager.WS.onStatusChanged = function () {
                _this.getPrinters().then(p => {
                    _this.printers = p;
                    _this.$nextTick();
                });
            };
        },

        getPrinters: function () {
            return new Promise((ok, err) => {
                let printers = [];
                if (
                    JSPM.JSPrintManager.websocket_status == JSPM.WSStatus.Open
                ) {
                    JSPM.JSPrintManager.getPrinters()
                        .then(function (myPrinters) {
                            printers = myPrinters;
                            console.log(printers);
                            ok(printers);
                        })
                        .catch(e => err(e));
                } else {
                    console.warn('JSPM WS not open');
                    ok(printers);
                }
            });
        },
    },

    mounted: function () {
        this.onInit();
        this.loadPendingTransactions();
    },
};
</script>

<style scoped>
.animate-spin {
    animation: spin 1s linear infinite;
}
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
